import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  Box,
  IconButton,
  TextField,
  Fade,
  InputAdornment,
  ClickAwayListener
} from "@mui/material";
import { Add, Edit, Delete, Clear } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { PropTypes } from "prop-types";
import MenuButton from "../Buttons/MenuButton";

const useTitleStyles = makeStyles(theme => ({
  titleInput: ({ variant }) => ({
    ...theme.components.MuiTypography.styleOverrides[variant]
  }),
  iconButton: {
    opacity: 1,
    transition: "opacity 300ms"
  },
  iconButtonHidden: {
    opacity: 0
  }
}));

const EditableTitle = ({
  title,
  onTitleChange,
  onDeleteButtonClick,
  onAddButtonClick,
  menuItemsForAddButton,
  addButtonDisabled,
  showIcons,
  typographyProps,
  placeholder
}) => {
  const classes = useTitleStyles({ variant: typographyProps.variant });
  const [editTitleMode, setEditTitleMode] = useState(false);
  const [titleValue, setTitleValue] = useState(title);
  const inputRef = useRef(null);
  const handleEditTitleClick = () => {
    setEditTitleMode(!editTitleMode);
  };

  useEffect(() => {
    if (editTitleMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editTitleMode]);

  useEffect(() => {
    setTitleValue(title);
  }, [title]);

  const handleTitleChange = () => {
    if (editTitleMode && title !== titleValue && onTitleChange) {
      onTitleChange(titleValue);
    }
    setEditTitleMode(false);
  };

  const handleCloseEditMode = () => {
    setEditTitleMode(false);
    setTitleValue(title);
  };
  return (
    <Box display="flex" alignItems="center" height={24} flex={1}>
      {editTitleMode ? (
        <ClickAwayListener onClickAway={handleTitleChange}>
          <TextField
            fullWidth
            value={titleValue}
            variant="filled"
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Box display="flex">
                    <IconButton
                      color="primary"
                      onClick={() => handleCloseEditMode()}
                      size="large"
                    >
                      <Clear />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
              className: classes.titleInput,
              onKeyDown: event => event.keyCode === 13 && handleTitleChange() // submits when enter is pressed
            }}
            onChange={e => setTitleValue(e.target.value)}
            inputRef={inputRef}
            hiddenLabel
          />
        </ClickAwayListener>
      ) : (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flex={1}
        >
          <Box display="flex" alignItems="center">
            <Typography className={classes.title} {...typographyProps}>
              {titleValue ? titleValue : placeholder}
            </Typography>
          </Box>
          <Fade in={showIcons} data-qa="editable-title-fade">
            <Box display="flex">
              {onAddButtonClick &&
                (!!menuItemsForAddButton ? (
                  <MenuButton
                    onClick={onAddButtonClick}
                    menuItems={menuItemsForAddButton}
                    isIconButton={true}
                  >
                    <Add color="primary" />
                  </MenuButton>
                ) : (
                  <IconButton onClick={onAddButtonClick} size="large">
                    <Add color="primary" />
                  </IconButton>
                ))}
              {onTitleChange && (
                <IconButton
                  onClick={handleEditTitleClick}
                  data-qa="editable-title-edit-btn"
                  size="large"
                >
                  <Edit color="primary" />
                </IconButton>
              )}
              {onDeleteButtonClick && (
                <IconButton onClick={onDeleteButtonClick} size="large">
                  <Delete color="primary" />
                </IconButton>
              )}
            </Box>
          </Fade>
        </Box>
      )}
    </Box>
  );
};

export default EditableTitle;

EditableTitle.propTypes = {
  placeholder: PropTypes.string,
  title: PropTypes.string,
  addButtonDisabled: PropTypes.bool
};

EditableTitle.defaultProps = {
  typographyProps: {
    variant: "h6"
  }
};
